/**
 * extractionStatusPolicy — SPO-1: one honest answer to "what terminal
 * `extractionStatus` does a scan outcome earn?".
 *
 * Extracted from `sedBootstrap.stampForcedScanStatus` so the policy is
 * pure and pinnable. The pre-fix ladder defaulted `attempts === 0` to
 * `'success-full'` ("nothing to extract — scanned, no gaps"), which is
 * right for a doc no section needs but a lie for a doc that HAS no
 * machine-readable text: six live Redhead scanned PDFs sat at
 * `needsOcr: true` with `[PDF: …]` placeholder text and a SCANNED chip.
 *
 * Policy (first match wins):
 *   1. zero successes + placeholder-only text  → 'failed-no-text'
 *   2. attempts === 0                          → 'success-full'
 *   3. successes === attempts (> 0)            → 'success-full'
 *   4. 0 < successes < attempts                → 'success-partial'
 *   5. successes === 0, attempts > 0           → lastError code or
 *                                                'failed-unknown'
 */
import type { ExtractionStatus } from '../../types/siteRag';
import { hasUsableExtractedText } from './extractedTextQuality';

/** Shared `extractionStatusError` copy for `'failed-no-text'` (upload
 *  stamp, forced-scan stamp, and the rebuild tail loop). */
export const NO_MACHINE_READABLE_TEXT_MESSAGE =
  'No machine-readable text — scanned/image-based document; OCR produced no text. Re-upload a text-based copy or retry OCR.';

export interface ScanOutcome {
  /** Sections/requirements the extractor was invoked for. */
  attempts: number;
  /** Invocations that returned data. */
  successes: number;
  /** Most recent classified failure, when any invocation threw. */
  lastError?: { code: ExtractionStatus; msg: string };
}

export interface ResolvedScanStatus {
  status: ExtractionStatus;
  errorMsg?: string;
}

export function resolveForcedScanStatus(
  outcome: ScanOutcome,
  docTextContent: string | undefined | null,
): ResolvedScanStatus {
  if (outcome.successes === 0 && !hasUsableExtractedText(docTextContent)) {
    return { status: 'failed-no-text', errorMsg: NO_MACHINE_READABLE_TEXT_MESSAGE };
  }
  if (outcome.attempts === 0) {
    return { status: 'success-full' };
  }
  if (outcome.successes >= outcome.attempts) {
    return { status: 'success-full' };
  }
  if (outcome.successes > 0) {
    return { status: 'success-partial' };
  }
  return {
    status: outcome.lastError?.code ?? 'failed-unknown',
    errorMsg: outcome.lastError?.msg ?? 'No SED section could be extracted from this document.',
  };
}
